import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';

/**
 * Background prefetch hook for agent enhanced data
 *
 * Fetches /agent/enhanced after page load and populates individual
 * agent caches for instant drawer opens.
 *
 * Architecture:
 * - Table uses ONLY /agent/list (fast, 1-2s page load)
 * - This hook starts after page renders
 * - Populates individual agent caches with setQueryData
 * - Drawer reads from prefetched cache -> instant (0ms) opens with full data
 *
 * @param agents - Array of agents from basic query (used to determine when to prefetch)
 */
export function useAgentBackgroundPrefetch(agents: Agent[]) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  // Only prefetch for reasonable agent counts
  // Disabled if no agents loaded yet or too many agents
  const shouldPrefetch = agents.length > 0 && agents.length < 100;

  return useQuery({
    queryKey: ['agents', 'enhanced', 'background'],
    queryFn: async () => {
      const response = await axios.get<Agent[]>('/agent/enhanced');

      // CRITICAL: Populate individual caches for instant drawer opens
      // This matches the query key used by useAgentDrawerData
      response.data.forEach(agent => {
        queryClient.setQueryData(['agent', agent.client_id, 'enhanced'], agent);
      });

      return response.data;
    },
    enabled: shouldPrefetch,
    staleTime: 60_000, // 1 minute
    gcTime: 300_000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: true,
  });
}
